import { isEmpty } from 'lodash-es';
import { forwardRef, type Attributes, type PropsWithChildren } from 'react';

import { svgWrapperCSS } from './GanttChartLineRenderer.sty.js';
import { GanttSegment } from './GanttSegment.js';
import { ChartStateHandler } from '../../core/components/chart-states/ChartStateHandler.js';
import { getChartStatesTemplates } from '../../core/components/chart-states/utils/get-chart-states-template.js';
import { EmptyStateSlot } from '../../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../../core/slots/chart-states/ErrorStateSlot.js';
import {
  DEFAULT_GANTT_COLORS,
  DISABLED_PATTERN_ANGLE,
  DISABLED_PATTERN_SIZE,
  STRIPES_PATTERN_ID,
} from '../constants/defaults.js';
import { useGanttSegmentsContext } from '../context/GanttSegmentsContext.js';

interface GanttLineRendererProps extends Attributes {
  width: number;
  height: number;
  loading?: boolean;
  showBackground?: boolean;
}

const _GanttChartLineRenderer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<GanttLineRendererProps>
>((props, forwardedRef) => {
  const { width, height: svgHeight, children } = props;
  const { segmentsData } = useGanttSegmentsContext();
  const backgroundSegments: { start: number; end: number }[] = [];
  const { emptyState, errorState } = getChartStatesTemplates(children);

  // if showBackground is enabled, create an array of background segments to draw between the data segements
  if (props.showBackground) {
    segmentsData.sort((a, b) => a.start - b.start);
    for (let i = 0; i < segmentsData.length - 1; i++) {
      const currentSegmentEnd = segmentsData[i].end || segmentsData[i].start;
      const nextSegmentStart = segmentsData[i + 1].start;
      if (currentSegmentEnd < nextSegmentStart) {
        backgroundSegments.push({
          start: currentSegmentEnd,
          end: nextSegmentStart,
        });
      }
    }
  }

  return (
    <ChartStateHandler
      empty={isEmpty(segmentsData)}
      height="100%"
      emptyState={emptyState}
      errorState={errorState}
      isLoading={props.loading === true}
    >
      <div className={svgWrapperCSS} ref={forwardedRef}>
        <svg width={width} height={svgHeight}>
          {backgroundSegments.length > 0 && (
            <defs>
              <pattern
                id={STRIPES_PATTERN_ID}
                patternUnits="userSpaceOnUse"
                width={DISABLED_PATTERN_SIZE * 2}
                height={DISABLED_PATTERN_SIZE * 2}
                patternTransform={`rotate(${DISABLED_PATTERN_ANGLE})`}
              >
                <rect
                  x="0"
                  y="0"
                  width={DISABLED_PATTERN_SIZE}
                  height={DISABLED_PATTERN_SIZE * 2}
                  fill={DEFAULT_GANTT_COLORS.background}
                ></rect>
                <rect
                  x={DISABLED_PATTERN_SIZE}
                  y="0"
                  width={DISABLED_PATTERN_SIZE}
                  height={DISABLED_PATTERN_SIZE * 2}
                  fill="transparent"
                ></rect>
              </pattern>
            </defs>
          )}
          {backgroundSegments.map((segment, index) => (
            <GanttSegment
              key={index}
              containerWidth={width}
              start={segment.start}
              end={segment.end}
              height={svgHeight}
            />
          ))}
          <g>
            {segmentsData.map((entry, index) => (
              <GanttSegment
                key={index}
                containerWidth={width}
                height={svgHeight}
                data={entry}
                start={entry.start}
                end={entry.end}
              />
            ))}
          </g>
        </svg>
      </div>
    </ChartStateHandler>
  );
});

/**
 * @internal
 */
export const GanttChartLineRenderer = Object.assign(_GanttChartLineRenderer, {
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
